import type { WriteOffKind, WriteOffRowKind } from '@didto/domain';
import {
  WRITE_OFF_LABEL,
  WRITE_OFF_REASON_MAX,
  canWalkOut,
  formatMinor,
  fractionDigitsFor,
  minorToInput,
  parseMoneyToMinor,
  writeOffBalanceMinor,
  writeOffReasonError,
} from '@didto/domain';
import { Badge, Button, Field, Input, KvRow, Select, Sheet, Textarea, Toggle } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useOnline } from '@/lib/network';
import type { Order } from '@/lib/orders';
import { supabase } from '@/lib/supabase';
import './shift.css';

/**
 * Write-offs on the POS (Phase 7B-1, ADR-031): a walk-out (the whole balance of a served order), a staff meal (an
 * employee's food, up to the balance) and the owner's comp. Never a payment: the order settles as written off. The
 * server decides who may act alone; beyond the cashier's allowance, and for a comp, its refusal names
 * writeoff.approve and the counter asks the owner for a grant (ADR-023). Writing off or reinstating needs the server:
 * while it cannot be reached, neither can be started (ADR-037).
 */

export interface WriteOffRow {
  id: string;
  kind: WriteOffRowKind;
  amount_minor: number;
  reason: string;
  employee_id: string | null;
  authority: 'cashier' | 'owner';
  recorded_by_employee_id: string;
  approved_by_employee_id: string | null;
  reinstates_id: string | null;
  recorded_at: string;
}

/** Under ['orders'], so every command's refresh of the orders refreshes these too. */
export const writeOffKeys = {
  order: (orderId: string) => ['orders', 'write-offs', orderId] as const,
};

export function useOrderWriteOffs(orderId: string | null) {
  return useQuery({
    queryKey: writeOffKeys.order(orderId ?? ''),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('order_write_off')
        .select(
          'id, kind, amount_minor, reason, employee_id, authority, recorded_by_employee_id, approved_by_employee_id, reinstates_id, recorded_at',
        )
        .eq('order_id', orderId ?? '')
        .order('recorded_at');
      if (error) throw error;
      return (data ?? []) as WriteOffRow[];
    },
  });
}

/** Active employees, for "who ate" (the tablet reads them as its PIN screen does). */
export function usePickerEmployees(enabled: boolean) {
  return useQuery({
    queryKey: ['picker-employees', 'write-off'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee')
        .select('id, display_name')
        .eq('status', 'active')
        .order('display_name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** The write-offs of an order, each reinstatable once, and the actions while something is owed. */
export function WriteOffsPanel({
  order,
  rows,
  currency,
  canWriteOff,
  nameOf,
  onStart,
  onReinstate,
}: {
  order: Order;
  rows: WriteOffRow[];
  currency: string;
  canWriteOff: boolean;
  nameOf: (employeeId: string | null) => string;
  onStart: (kind: WriteOffKind) => void;
  onReinstate: (row: WriteOffRow) => void;
}) {
  const open = order.status === 'open';
  const balance = writeOffBalanceMinor(order);
  const reinstated = new Set(rows.filter((r) => r.reinstates_id).map((r) => r.reinstates_id));
  const live = open || order.status === 'completed';
  const online = useOnline();
  return (
    <div className="d-stack d-stack--tight" data-testid="write-offs">
      {rows.length > 0 && (
        <>
          <div className="d-eyebrow">Written off</div>
          <div className="d-list">
            {rows.map((r) => (
              <div className="d-cartline" key={r.id} data-testid="write-off-row">
                <div className="d-cartline__main">
                  <div className="d-row__title">{WRITE_OFF_LABEL[r.kind]}</div>
                  <div className="d-row__meta">
                    {r.kind === 'staff_meal' ? `${nameOf(r.employee_id)} · ` : ''}
                    {r.reason} · by {nameOf(r.recorded_by_employee_id)}
                    {r.approved_by_employee_id &&
                    r.approved_by_employee_id !== r.recorded_by_employee_id
                      ? ` · approved by ${nameOf(r.approved_by_employee_id)}`
                      : ''}
                  </div>
                </div>
                <div className="d-cartline__side">
                  <strong className="d-mono">{formatMinor(r.amount_minor, currency)}</strong>
                  {r.authority === 'owner' && r.kind !== 'reinstated' && (
                    <Badge tone="info">owner</Badge>
                  )}
                  {reinstated.has(r.id) && <Badge>reinstated</Badge>}
                  {r.kind !== 'reinstated' && !reinstated.has(r.id) && live && canWriteOff && (
                    <Button
                      variant="quiet"
                      size="sm"
                      disabled={!online}
                      onClick={() => onReinstate(r)}
                      data-testid="write-off-reinstate"
                    >
                      Reinstate
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {open && balance > 0 && canWriteOff && (
        <div className="d-actions" data-testid="write-off-actions">
          {canWalkOut(order.fulfilment_status) && (
            <Button
              variant="quiet"
              disabled={!online}
              onClick={() => onStart('walkout')}
              data-testid="write-off-walkout"
            >
              Walk-out
            </Button>
          )}
          <Button
            variant="quiet"
            disabled={!online}
            onClick={() => onStart('staff_meal')}
            data-testid="write-off-meal"
          >
            Staff meal
          </Button>
          <Button
            variant="quiet"
            disabled={!online}
            onClick={() => onStart('comp')}
            data-testid="write-off-comp"
          >
            Comp
          </Button>
        </div>
      )}
    </div>
  );
}

export interface WriteOffInput {
  kind: WriteOffKind;
  amount_minor?: number;
  employee_id?: string;
  reason: string;
}

const KIND_HELP: Record<WriteOffKind, string> = {
  walkout:
    'The guests left without paying for what they were served. The whole balance is written off; it is not money received.',
  staff_meal:
    'An employee’s meal, up to what is owed. The rest of the order is paid as usual. Flagged for the owner.',
  comp: 'Given by the owner: waived, not paid. A cashier’s comp waits for the owner’s approval.',
};

export function WriteOffSheet({
  kind,
  order,
  currency,
  employees,
  defaultEmployeeId,
  busy,
  onClose,
  onSubmit,
}: {
  kind: WriteOffKind | null;
  order: Order | null;
  currency: string;
  employees: { id: string; display_name: string }[];
  defaultEmployeeId: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: WriteOffInput) => Promise<void>;
}) {
  return (
    <Sheet
      open={!!kind && !!order}
      onClose={onClose}
      title={kind ? (kind === 'walkout' ? 'Walk-out' : WRITE_OFF_LABEL[kind]) : ''}
    >
      {kind && order && (
        <WriteOffForm
          key={`${order.id}:${kind}:${order.version}`}
          kind={kind}
          order={order}
          currency={currency}
          employees={employees}
          defaultEmployeeId={defaultEmployeeId}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Sheet>
  );
}

function WriteOffForm({
  kind,
  order,
  currency,
  employees,
  defaultEmployeeId,
  busy,
  onClose,
  onSubmit,
}: {
  kind: WriteOffKind;
  order: Order;
  currency: string;
  employees: { id: string; display_name: string }[];
  defaultEmployeeId: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: WriteOffInput) => Promise<void>;
}) {
  const fd = fractionDigitsFor(currency);
  const balance = writeOffBalanceMinor(order);
  const [amount, setAmount] = useState(minorToInput(balance, fd));
  const [employeeId, setEmployeeId] = useState(defaultEmployeeId ?? '');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const online = useOnline();
  const amountMinor =
    kind === 'walkout' ? balance : (parseMoneyToMinor(amount, { fractionDigits: fd }) ?? 0);
  const reasonError = writeOffReasonError(reason);
  const amountError =
    amountMinor <= 0
      ? 'Enter an amount'
      : amountMinor > balance
        ? `At most ${formatMinor(balance, currency)}`
        : null;
  const employeeError = kind === 'staff_meal' && !employeeId ? 'Choose who ate' : null;
  const error = reasonError ?? amountError ?? employeeError;
  const submit = () => {
    setTouched(true);
    if (error) return;
    void onSubmit({
      kind,
      reason: reason.trim(),
      ...(kind === 'walkout' ? { amount_minor: balance } : { amount_minor: amountMinor }),
      ...(kind === 'staff_meal' ? { employee_id: employeeId } : {}),
    });
  };
  return (
    <div className="d-stack" data-testid="write-off-sheet">
      <p className="d-muted">{KIND_HELP[kind]}</p>
      <div className="d-kv">
        <KvRow label="Order" value={<span className="d-mono">{order.order_number}</span>} />
        <KvRow
          label="Owed"
          value={<strong className="d-mono">{formatMinor(balance, currency)}</strong>}
        />
      </div>
      {kind === 'staff_meal' && (
        <Field label="Who ate">
          {(id) => (
            <Select
              id={id}
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              data-testid="write-off-employee"
            >
              <option value="">Choose an employee…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.display_name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {kind !== 'walkout' && (
        <Field label="Amount" help={`Up to ${formatMinor(balance, currency)}.`}>
          {(id) => (
            <Input
              id={id}
              className="d-input--money"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="write-off-amount"
            />
          )}
        </Field>
      )}
      <Field label="Reason" help="Required; it is kept with the write-off and in the audit log.">
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            maxLength={WRITE_OFF_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="write-off-reason"
          />
        )}
      </Field>
      {touched && error && (
        <p className="d-error" role="alert">
          {error}
        </p>
      )}
      <div className="d-sheet__actions">
        <Button variant="quiet" onClick={onClose}>
          Back
        </Button>
        <Button
          variant="danger-solid"
          busy={busy}
          disabled={!online}
          onClick={submit}
          data-testid="write-off-confirm"
        >
          Write off {formatMinor(amountMinor > 0 ? amountMinor : 0, currency)}
        </Button>
      </div>
    </div>
  );
}

/** A write-off owed again: collected now in cash, or left owed (taken later with Pay). */
export function ReinstateSheet({
  row,
  currency,
  canCollectCash,
  busy,
  onClose,
  onSubmit,
}: {
  row: WriteOffRow | null;
  currency: string;
  /** Only when the write-off is all that will be owed and a cash method is enabled. */
  canCollectCash: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { reason: string; collectCash: boolean }) => Promise<void>;
}) {
  return (
    <Sheet open={!!row} onClose={onClose} title="Reinstate">
      {row && (
        <ReinstateForm
          key={row.id}
          row={row}
          currency={currency}
          canCollectCash={canCollectCash}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Sheet>
  );
}

function ReinstateForm({
  row,
  currency,
  canCollectCash,
  busy,
  onClose,
  onSubmit,
}: {
  row: WriteOffRow;
  currency: string;
  canCollectCash: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { reason: string; collectCash: boolean }) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [collect, setCollect] = useState(true);
  const [touched, setTouched] = useState(false);
  const online = useOnline();
  const error = writeOffReasonError(reason);
  const collecting = canCollectCash && collect;
  return (
    <div className="d-stack" data-testid="reinstate-sheet">
      <p>
        {WRITE_OFF_LABEL[row.kind]} of{' '}
        <strong className="d-mono">{formatMinor(row.amount_minor, currency)}</strong> is owed again.
        The day’s allowance stays used.
      </p>
      {canCollectCash && (
        <Toggle
          checked={collect}
          onChange={setCollect}
          label={`Collected now: ${formatMinor(row.amount_minor, currency)} in cash`}
        />
      )}
      <Field label="Reason" help="Required; for example: came back and paid.">
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            maxLength={WRITE_OFF_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reinstate-reason"
          />
        )}
      </Field>
      {touched && error && (
        <p className="d-error" role="alert">
          {error}
        </p>
      )}
      <div className="d-sheet__actions">
        <Button variant="quiet" onClick={onClose}>
          Back
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!online}
          onClick={() => {
            setTouched(true);
            if (error) return;
            void onSubmit({ reason: reason.trim(), collectCash: collecting });
          }}
          data-testid="reinstate-confirm"
        >
          {collecting
            ? `Reinstate and collect ${formatMinor(row.amount_minor, currency)}`
            : 'Reinstate'}
        </Button>
      </div>
    </div>
  );
}
